import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, OCCASIONS } from "@/lib/api";
import PropertyCard from "../components/PropertyCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Stays() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [occasion, setOccasion] = useState(params.get("occasion") || "Any");
  const [guests, setGuests] = useState(params.get("guests") || "Any");
  const [managedOnly, setManagedOnly] = useState(params.get("managed") === "true");
  const [price, setPrice] = useState([5000, 45000]);

  useEffect(() => {
    const p = {};
    if (occasion !== "Any") p.occasion = occasion;
    if (guests !== "Any") p.guests = Number(guests);
    if (managedOnly) p.managed = true;
    p.min_price = price[0];
    p.max_price = price[1];
    setLoading(true);
    api.get("/properties", { params: p })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [occasion, guests, managedOnly, price]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (occasion !== "Any") next.set("occasion", occasion);
    if (guests !== "Any") next.set("guests", guests);
    if (managedOnly) next.set("managed", "true");
    setParams(next, { replace: true });
  }, [occasion, guests, managedOnly]); // eslint-disable-line

  const reset = () => {
    setOccasion("Any");
    setGuests("Any");
    setManagedOnly(false);
    setPrice([5000, 45000]);
  };

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-24" data-testid="stays-page">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <div className="kicker mb-3">The marketplace</div>
          <h1 className="font-display text-4xl md:text-6xl text-[#0E2E24] tracking-tight font-semibold leading-[1.05]">
            Curated stays in <span className="text-[#1F5A43]">Hyderabad.</span>
          </h1>
          <p className="mt-4 text-[#111111]/70 max-w-xl">
            Every property on Appy Stays is visited, vetted and photographed by us. {items.length} homes ready for your next celebration.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 lg:sticky lg:top-24 self-start bg-white rounded-3xl p-7 border border-[#0E2E24]/10" data-testid="stays-filters">
          <div className="flex items-center justify-between mb-6">
            <span className="font-display text-xl text-[#0E2E24]">Filter</span>
            <button onClick={reset} className="text-xs font-sora text-[#1F5A43] hover:underline" data-testid="stays-reset">Reset</button>
          </div>

          <div className="space-y-8">
            <div>
              <Label className="kicker mb-3 block">Occasion</Label>
              <Select value={occasion} onValueChange={setOccasion}>
                <SelectTrigger data-testid="filter-occasion"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Any">Any occasion</SelectItem>
                  {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="kicker mb-3 block">Guests</Label>
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger data-testid="filter-guests"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Any">Any group size</SelectItem>
                  <SelectItem value="6">Up to 6</SelectItem>
                  <SelectItem value="12">Up to 12</SelectItem>
                  <SelectItem value="20">Up to 20</SelectItem>
                  <SelectItem value="30">30+ guests</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="kicker mb-3 block">Price / night</Label>
              <Slider
                value={price}
                onValueChange={setPrice}
                min={5000}
                max={45000}
                step={500}
                data-testid="filter-price"
              />
              <div className="mt-3 flex justify-between text-xs font-sora text-[#111111]/70">
                <span>₹{price[0].toLocaleString("en-IN")}</span>
                <span>₹{price[1].toLocaleString("en-IN")}</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="managed-only" className="font-sora text-sm text-[#0E2E24]">Managed by Appy only</Label>
              <Switch id="managed-only" checked={managedOnly} onCheckedChange={setManagedOnly} data-testid="filter-managed" />
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9">
          {loading && <div className="text-[#111111]/60 py-20 text-center font-sora">Loading stays…</div>}
          {!loading && items.length === 0 && (
            <div className="py-20 text-center">
              <p className="font-display text-2xl text-[#0E2E24]">Nothing matches — yet.</p>
              <p className="mt-2 text-[#111111]/60">Try widening your filters or resetting.</p>
              <Button onClick={reset} className="mt-6 rounded-full bg-[#0E2E24] text-[#FAFAF8]">Reset filters</Button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="stays-grid">
            {items.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        </section>
      </div>
    </main>
  );
}
